// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Separator,
  useSnackBar,
  useUniqueId,
} from '@usealloy/component-library';
import { IsolatedEditorContainer } from 'flow/editor/styles';
import {
  StyledTipCallout,
  StyledAggregationCriteriaContainer,
  StyledAggregationSubEditorLayout,
  StyledAggregationSubEditorRow,
  StyledAggregationSubEditorActions,
} from './styles';
import BaseAggregationsSection from './BaseAggregationsSection';
import AggregationFiltersSection from './AggregationFiltersSection';
import AggregationNameAndDescription from './AggregationNameAndDescription';
import { AggregationEditorContext } from './context';
import {
  getValidationErrors,
  hasIncompleteFilters,
  getTimeframeMaxExceededMessage,
} from './helpers';
import type { AggregationValidationErrors } from './types';

const AggregationsSubEditorContainer = () => {
  const {
    sourceData,
    handleSave,
    handleCancel,
    aggregationEditorContainerRef,
    isSaveDisabled,
    keyboardFocus,
    ...restContext
  } = useContext(AggregationEditorContext);

  const addSnackBar = useSnackBar();
  const [validationErrors, setValidationErrors] = useState<AggregationValidationErrors>({});

  const handleDone = () => {
    const errors = getValidationErrors(sourceData);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const timeframeMessage = getTimeframeMaxExceededMessage(
      sourceData.scope,
      sourceData.timeframeUnit,
      sourceData.timeframeIntervalEnd,
    );
    const hasFilterErrors = hasIncompleteFilters(sourceData.filters);
    const hasSaveBlockingErrors = hasFilterErrors || timeframeMessage != null;

    if (hasSaveBlockingErrors) {
      const snackbarTitle = hasFilterErrors
        ? 'Please ensure all filters have values and the timeframe is within the allowed maximum.'
        : timeframeMessage;
      addSnackBar({ title: snackbarTitle, type: 'danger' });
      return;
    }

    handleSave();
  };

  const innerContextValue = useMemo(() => ({
    ...restContext,
    sourceData,
    handleSave,
    handleCancel,
    aggregationEditorContainerRef,
    isSaveDisabled,
    validationErrors,
    keyboardFocus,
  }), [
    restContext,
    sourceData,
    handleSave,
    handleCancel,
    aggregationEditorContainerRef,
    isSaveDisabled,
    validationErrors,
    keyboardFocus,
  ]);


  const cancelRef = useRef<HTMLButtonElement>(null);
  const cancelId = useUniqueId('aggregation-cancel-btn');

  const doneRef = useRef<HTMLButtonElement>(null);
  const doneId = useUniqueId('aggregation-done-btn');

  useEffect(() => {
    keyboardFocus?.register({ id: cancelId, ref: cancelRef });
    keyboardFocus?.register({ id: doneId, ref: doneRef });

    return () => {
      keyboardFocus?.unregister(cancelId);
      keyboardFocus?.unregister(doneId);
    };
  }, [
    cancelId,
    cancelRef,
    doneRef,
    doneId,
    keyboardFocus,
  ]);


  return (
    <AggregationEditorContext.Provider value={innerContextValue}>
      <IsolatedEditorContainer ref={aggregationEditorContainerRef}>
        <StyledAggregationSubEditorLayout>
          <StyledAggregationSubEditorRow>
            <StyledAggregationCriteriaContainer>
              <StyledTipCallout text="Build your custom aggregation by selecting the options below." />
              <BaseAggregationsSection />
              <AggregationFiltersSection />
            </StyledAggregationCriteriaContainer>
            <AggregationNameAndDescription />
          </StyledAggregationSubEditorRow>
          <Separator />
          <StyledAggregationSubEditorActions>
            <Button
              ref={cancelRef}
              size={ButtonSize.small}
              variant={ButtonVariant.secondary}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              ref={doneRef}
              size={ButtonSize.small}
              variant={ButtonVariant.primary}
              onClick={handleDone}
            >
              Done
            </Button>
          </StyledAggregationSubEditorActions>
        </StyledAggregationSubEditorLayout>
      </IsolatedEditorContainer>
    </AggregationEditorContext.Provider>
  );
};

export default AggregationsSubEditorContainer;
